import type { AmbientShellBackgroundConfig } from './ambientShellBackground'
import {
  ACTIVE_AMBIENT_SHELL_BACKGROUND,
  ambientShellBackgroundsEqual,
  AMBIENT_SHELL_NO_LIGHT_BEAMS,
  DEFAULT_AMBIENT_SHELL_BACKGROUND,
  END_AMBIENT_SHELL_BACKGROUND,
  GOLD_RUSH_AMBIENT_SHELL_BACKGROUND,
  lerpAmbientShellBackground,
  mergeAmbientShellBackground,
  PRESSURE_AMBIENT_SHELL_BACKGROUND,
} from './ambientShellBackground'

export type { AmbientShellBackgroundConfig } from './ambientShellBackground'

export type AmbientPresetId = 'home' | 'focus' | 'active' | 'pressure' | 'gold-rush' | 'end'

/** Stable order for UI (e.g. dev preset picker). */
export const AMBIENT_PRESET_IDS: AmbientPresetId[] = [
  'home',
  'focus',
  'active',
  'pressure',
  'gold-rush',
  'end',
]

export type AmbientMaxFps = number | 'uncapped'

export type AmbientParticlesConfig = {
  /** Number of particles kept alive across the batched Pixi graphics layers. Keep this low. */
  particleCount: number
  /** Slowest particle travel speed in CSS pixels per second. */
  speedMin: number
  /** Fastest particle travel speed in CSS pixels per second. */
  speedMax: number
  /** Global particle alpha multiplier, from 0 to 1. */
  opacity: number
  /** Per-particle minimum peak alpha before the global opacity multiplier. */
  alphaMin: number
  /** Per-particle maximum peak alpha before the global opacity multiplier. */
  alphaMax: number
  /** Smallest particle radius in CSS pixels. */
  sizeMin: number
  /** Largest particle radius in CSS pixels. */
  sizeMax: number
  /** Shortest fade-in or fade-out duration in milliseconds. */
  fadeDurationMinMs: number
  /** Longest fade-in or fade-out duration in milliseconds. */
  fadeDurationMaxMs: number
  /** Shortest full-alpha lifetime in milliseconds. */
  fullAlphaDurationMinMs: number
  /** Longest full-alpha lifetime in milliseconds. */
  fullAlphaDurationMaxMs: number
  /** Extra horizontal drift in CSS pixels per second, applied before wrapping. */
  driftX: number
  /** Extra vertical drift in CSS pixels per second, applied before wrapping. */
  driftY: number
  /** Offscreen spawn/wrap padding as a viewport ratio. */
  spawnPaddingRatio: number
  /** Base HSL hue in degrees. */
  baseHue: number
  /** Per-tone hue spread in degrees across the three tone buckets. */
  hueShift: number
  /** Extra per-particle hue offset in degrees (sampled between min and max). */
  hueJitterMinDeg: number
  hueJitterMaxDeg: number
  /** HSL saturation in percent (0-100), sampled per particle between min and max. */
  saturationMin: number
  saturationMax: number
  /** HSL lightness in percent (0-100), sampled per particle between min and max. */
  lightnessMin: number
  lightnessMax: number
  /** Caps ticker work when set; `uncapped` follows requestAnimationFrame pacing. */
  maxFps: AmbientMaxFps
  /** Optional deterministic seed for repeatable development tuning. */
  seed?: number | string
  /** Full-screen base + two radial layers (legacy RouteShell CSS). */
  shellBackground: AmbientShellBackgroundConfig
}

/** Default duration when blending ambient presets (browser shell / RouteShell). */
export const BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS = 500

export const DEFAULT_AMBIENT_PARTICLES_CONFIG: AmbientParticlesConfig = {
  alphaMax: 0.95,
  alphaMin: 0.28,
  baseHue: 42,
  driftX: 1.2,
  driftY: -0.4,
  fadeDurationMaxMs: 2250,
  fadeDurationMinMs: 850,
  fullAlphaDurationMaxMs: 500,
  fullAlphaDurationMinMs: 400,
  hueJitterMaxDeg: 0,
  hueJitterMinDeg: 0,
  hueShift: 24,
  maxFps: 'uncapped',
  opacity: 0.2,
  particleCount: 28,
  lightnessMax: 72,
  lightnessMin: 72,
  saturationMax: 78,
  saturationMin: 78,
  seed: 'boggle-home',
  sizeMax: 30,
  sizeMin: 0.7,
  speedMax: 30,
  speedMin: 5,
  spawnPaddingRatio: 0.08,
  shellBackground: DEFAULT_AMBIENT_SHELL_BACKGROUND,
}

export const AMBIENT_PRESETS: Record<AmbientPresetId, Partial<AmbientParticlesConfig>> = {
  // Tuned for the landing/lobby shell: barely-there warm motes behind the radial layers.
  home: {
    alphaMax: 0.5,
    alphaMin: 0.3,
    baseHue: 42,
    driftX: 1.2,
    driftY: -0.4,
    fadeDurationMaxMs: 2250,
    fadeDurationMinMs: 850,
    fullAlphaDurationMaxMs: 3000,
    fullAlphaDurationMinMs: 100,
    hueJitterMaxDeg: 12,
    hueJitterMinDeg: -12,
    hueShift: 24,
    maxFps: 'uncapped',
    opacity: 1,
    particleCount: 20,
    lightnessMax: 72,
    lightnessMin: 46,
    saturationMax: 92,
    saturationMin: 62,
    seed: 'boggle-home',
    sizeMax: 40,
    sizeMin: 0.3,
    speedMax: 12,
    speedMin: 5,
    spawnPaddingRatio: 0.08,
  },
  // Stub for calmer in-run concentration.
  focus: {
    shellBackground: mergeAmbientShellBackground(
      DEFAULT_AMBIENT_SHELL_BACKGROUND,
      AMBIENT_SHELL_NO_LIGHT_BEAMS,
    ),
    alphaMax: 0.6,
    alphaMin: 0.1,
    baseHue: 42,
    driftX: 1.2,
    driftY: -0.4,
    fullAlphaDurationMaxMs: 500,
    fullAlphaDurationMinMs: 400,
    hueJitterMaxDeg: 12,
    hueJitterMinDeg: -12,
    hueShift: 24,
    lightnessMax: 72,
    lightnessMin: 46,
    opacity: 0.3,
    saturationMax: 92,
    saturationMin: 62,
    particleCount: 30,
    seed: 'boggle-home',
    sizeMax: 40,
    sizeMin: 0.3,
    speedMax: 30,
    speedMin: 5,
  },
  // Late-clock / high-energy shell: warmer base, larger radials, amber top-right glow.
  active: {
    shellBackground: mergeAmbientShellBackground(
      ACTIVE_AMBIENT_SHELL_BACKGROUND,
      AMBIENT_SHELL_NO_LIGHT_BEAMS,
    ),
    alphaMax: 0.4,
    alphaMin: 0.2,
    baseHue: 32,
    driftX: 1.2,
    driftY: -0.4,
    fadeDurationMaxMs: 2250,
    fadeDurationMinMs: 850,
    fullAlphaDurationMaxMs: 3000,
    fullAlphaDurationMinMs: 100,
    hueJitterMaxDeg: 12,
    hueJitterMinDeg: -12,
    hueShift: 24,
    maxFps: 'uncapped',
    opacity: 0.5,
    particleCount: 50,
    lightnessMax: 72,
    lightnessMin: 46,
    saturationMax: 92,
    saturationMin: 62,
    seed: 'boggle-home',
    sizeMax: 62.9,
    sizeMin: 17.4,
    speedMax: 60.4,
    speedMin: 33.1,
    spawnPaddingRatio: 0.08,
  },
  // Stub for late-clock or high-stakes tension.
  pressure: {
    shellBackground: mergeAmbientShellBackground(
      PRESSURE_AMBIENT_SHELL_BACKGROUND,
      AMBIENT_SHELL_NO_LIGHT_BEAMS,
    ),
    alphaMax: 0.96,
    alphaMin: 0.71,
    baseHue: 7,
    driftX: 1.2,
    driftY: -0.4,
    fadeDurationMaxMs: 900,
    fadeDurationMinMs: 500,
    fullAlphaDurationMaxMs: 500,
    fullAlphaDurationMinMs: 400,
    hueJitterMaxDeg: 8,
    hueJitterMinDeg: -8,
    hueShift: 21,
    maxFps: 60,
    opacity: 0.25,
    particleCount: 49,
    lightnessMax: 32,
    lightnessMin: 15,
    saturationMax: 66,
    saturationMin: 42,
    seed: 'boggle-home',
    sizeMax: 100,
    sizeMin: 4.5,
    speedMax: 56.8,
    speedMin: 30.5,
    spawnPaddingRatio: 0.08,
  },
  // Stub for end of game warning.
  end: {
    shellBackground: mergeAmbientShellBackground(
      END_AMBIENT_SHELL_BACKGROUND,
      AMBIENT_SHELL_NO_LIGHT_BEAMS,
    ),
    alphaMax: 0.96,
    alphaMin: 0.71,
    baseHue: 7,
    driftX: 1.2,
    driftY: -0.4,
    fadeDurationMaxMs: 900,
    fadeDurationMinMs: 500,
    fullAlphaDurationMaxMs: 500,
    fullAlphaDurationMinMs: 400,
    hueJitterMaxDeg: 8,
    hueJitterMinDeg: -8,
    hueShift: 21,
    maxFps: 60,
    opacity: 0.2,
    particleCount: 10,
    lightnessMax: 32,
    lightnessMin: 15,
    saturationMax: 66,
    saturationMin: 42,
    seed: 'boggle-home',
    sizeMax: 100,
    sizeMin: 4.5,
    speedMax: 56.8,
    speedMin: 30.5,
    spawnPaddingRatio: 0.08,
  },
  // Stub for Gold Rush/overtime energy; gameplay can tune this once phases settle.
  'gold-rush': {
    shellBackground: mergeAmbientShellBackground(
      GOLD_RUSH_AMBIENT_SHELL_BACKGROUND,
      AMBIENT_SHELL_NO_LIGHT_BEAMS,
    ),
    alphaMax: 0.96,
    alphaMin: 0.71,
    baseHue: 50,
    driftX: 1.2,
    driftY: -0.4,
    fadeDurationMaxMs: 1300,
    fadeDurationMinMs: 1100,
    fullAlphaDurationMaxMs: 1700,
    fullAlphaDurationMinMs: 400,
    hueJitterMaxDeg: 3,
    hueJitterMinDeg: -8,
    hueShift: 21,
    maxFps: 60,
    opacity: 0.1,
    particleCount: 49,
    lightnessMax: 52,
    lightnessMin: 25,
    saturationMax: 56,
    saturationMin: 31,
    seed: 'boggle-home',
    sizeMax: 77.1,
    sizeMin: 53.8,
    speedMax: 100,
    speedMin: 62.5,
    spawnPaddingRatio: 0.08,
  },
}

/** True when instant config swap can skip a full particle rebuild (same simulation + draw parameters). */
export function areAmbientParticleConfigsEqual(
  a: AmbientParticlesConfig,
  b: AmbientParticlesConfig,
): boolean {
  if (a === b) {
    return true
  }
  return (
    a.particleCount === b.particleCount &&
    a.speedMin === b.speedMin &&
    a.speedMax === b.speedMax &&
    a.opacity === b.opacity &&
    a.alphaMin === b.alphaMin &&
    a.alphaMax === b.alphaMax &&
    a.sizeMin === b.sizeMin &&
    a.sizeMax === b.sizeMax &&
    a.fadeDurationMinMs === b.fadeDurationMinMs &&
    a.fadeDurationMaxMs === b.fadeDurationMaxMs &&
    a.fullAlphaDurationMinMs === b.fullAlphaDurationMinMs &&
    a.fullAlphaDurationMaxMs === b.fullAlphaDurationMaxMs &&
    a.driftX === b.driftX &&
    a.driftY === b.driftY &&
    a.spawnPaddingRatio === b.spawnPaddingRatio &&
    a.baseHue === b.baseHue &&
    a.hueShift === b.hueShift &&
    a.hueJitterMinDeg === b.hueJitterMinDeg &&
    a.hueJitterMaxDeg === b.hueJitterMaxDeg &&
    a.saturationMin === b.saturationMin &&
    a.saturationMax === b.saturationMax &&
    a.lightnessMin === b.lightnessMin &&
    a.lightnessMax === b.lightnessMax &&
    a.maxFps === b.maxFps &&
    a.seed === b.seed &&
    ambientShellBackgroundsEqual(a.shellBackground, b.shellBackground)
  )
}

export function resolveAmbientParticlesConfig(
  preset: AmbientPresetId = 'home',
  overrides: Partial<AmbientParticlesConfig> = {},
): AmbientParticlesConfig {
  const presetShell = AMBIENT_PRESETS[preset]?.shellBackground
  const shellBackground = mergeAmbientShellBackground(
    mergeAmbientShellBackground(DEFAULT_AMBIENT_SHELL_BACKGROUND, presetShell),
    overrides.shellBackground,
  )

  const merged = {
    ...DEFAULT_AMBIENT_PARTICLES_CONFIG,
    ...AMBIENT_PRESETS[preset],
    ...overrides,
    shellBackground,
  }

  return normalizeAmbientParticlesConfig(merged as AmbientParticlesConfig)
}

/** Apply ordering/clamp invariants expected of a resolved config (used after preset merge or lerp). */
export function normalizeAmbientParticlesConfig(
  merged: AmbientParticlesConfig,
): AmbientParticlesConfig {
  return {
    ...merged,
    shellBackground: merged.shellBackground ?? DEFAULT_AMBIENT_SHELL_BACKGROUND,
    alphaMax: clamp(Math.max(merged.alphaMin, merged.alphaMax), 0, 1),
    alphaMin: clamp(Math.min(merged.alphaMin, merged.alphaMax), 0, 1),
    fadeDurationMaxMs: Math.max(
      100,
      Math.max(merged.fadeDurationMinMs, merged.fadeDurationMaxMs),
    ),
    fadeDurationMinMs: Math.max(
      100,
      Math.min(merged.fadeDurationMinMs, merged.fadeDurationMaxMs),
    ),
    fullAlphaDurationMaxMs: Math.max(
      100,
      Math.max(merged.fullAlphaDurationMinMs, merged.fullAlphaDurationMaxMs),
    ),
    fullAlphaDurationMinMs: Math.max(
      100,
      Math.min(merged.fullAlphaDurationMinMs, merged.fullAlphaDurationMaxMs),
    ),
    hueShift: Math.max(0, merged.hueShift),
    hueJitterMinDeg: Math.min(
      clamp(merged.hueJitterMinDeg, -180, 180),
      clamp(merged.hueJitterMaxDeg, -180, 180),
    ),
    hueJitterMaxDeg: Math.max(
      clamp(merged.hueJitterMinDeg, -180, 180),
      clamp(merged.hueJitterMaxDeg, -180, 180),
    ),
    lightnessMin: clamp(Math.min(merged.lightnessMin, merged.lightnessMax), 0, 100),
    lightnessMax: clamp(Math.max(merged.lightnessMin, merged.lightnessMax), 0, 100),
    saturationMin: clamp(Math.min(merged.saturationMin, merged.saturationMax), 0, 100),
    saturationMax: clamp(Math.max(merged.saturationMin, merged.saturationMax), 0, 100),
    maxFps:
      merged.maxFps === 'uncapped' ? 'uncapped' : Math.max(1, Math.round(merged.maxFps)),
    opacity: clamp(merged.opacity, 0, 1),
    particleCount: Math.max(0, Math.round(merged.particleCount)),
    sizeMax: Math.max(0.1, Math.max(merged.sizeMin, merged.sizeMax)),
    sizeMin: Math.max(0.1, Math.min(merged.sizeMin, merged.sizeMax)),
    speedMax: Math.max(0, Math.max(merged.speedMin, merged.speedMax)),
    speedMin: Math.max(0, Math.min(merged.speedMin, merged.speedMax)),
    spawnPaddingRatio: clamp(merged.spawnPaddingRatio, 0, 0.5),
  }
}

/**
 * Blend two resolved configs for smooth ambient transitions. Pass linear progress in [0, 1];
 * easing is applied inside.
 * `maxFps` and `seed` follow the "from" config until t reaches 1, then match "to".
 */
export function lerpResolvedAmbientParticlesConfig(
  from: AmbientParticlesConfig,
  to: AmbientParticlesConfig,
  tLinear: number,
): AmbientParticlesConfig {
  const t = clamp(tLinear, 0, 1)
  const u = easeInOutSineAmbient(t)

  const lerped: AmbientParticlesConfig = {
    particleCount: Math.round(lerpNumber(from.particleCount, to.particleCount, u)),
    speedMin: lerpNumber(from.speedMin, to.speedMin, u),
    speedMax: lerpNumber(from.speedMax, to.speedMax, u),
    opacity: lerpNumber(from.opacity, to.opacity, u),
    alphaMin: lerpNumber(from.alphaMin, to.alphaMin, u),
    alphaMax: lerpNumber(from.alphaMax, to.alphaMax, u),
    sizeMin: lerpNumber(from.sizeMin, to.sizeMin, u),
    sizeMax: lerpNumber(from.sizeMax, to.sizeMax, u),
    fadeDurationMinMs: lerpNumber(from.fadeDurationMinMs, to.fadeDurationMinMs, u),
    fadeDurationMaxMs: lerpNumber(from.fadeDurationMaxMs, to.fadeDurationMaxMs, u),
    fullAlphaDurationMinMs: lerpNumber(
      from.fullAlphaDurationMinMs,
      to.fullAlphaDurationMinMs,
      u,
    ),
    fullAlphaDurationMaxMs: lerpNumber(
      from.fullAlphaDurationMaxMs,
      to.fullAlphaDurationMaxMs,
      u,
    ),
    driftX: lerpNumber(from.driftX, to.driftX, u),
    driftY: lerpNumber(from.driftY, to.driftY, u),
    spawnPaddingRatio: lerpNumber(from.spawnPaddingRatio, to.spawnPaddingRatio, u),
    baseHue: lerpHueDegrees(from.baseHue, to.baseHue, u),
    hueShift: lerpNumber(from.hueShift, to.hueShift, u),
    hueJitterMinDeg: lerpNumber(from.hueJitterMinDeg, to.hueJitterMinDeg, u),
    hueJitterMaxDeg: lerpNumber(from.hueJitterMaxDeg, to.hueJitterMaxDeg, u),
    saturationMin: lerpNumber(from.saturationMin, to.saturationMin, u),
    saturationMax: lerpNumber(from.saturationMax, to.saturationMax, u),
    lightnessMin: lerpNumber(from.lightnessMin, to.lightnessMin, u),
    lightnessMax: lerpNumber(from.lightnessMax, to.lightnessMax, u),
    maxFps: t < 1 ? from.maxFps : to.maxFps,
    seed: t < 1 ? from.seed : to.seed,
    shellBackground: lerpAmbientShellBackground(
      from.shellBackground,
      to.shellBackground,
      u,
    ),
  }

  return normalizeAmbientParticlesConfig(lerped)
}

export function easeInOutSineAmbient(t: number): number {
  const c = clamp(t, 0, 1)
  return -(Math.cos(Math.PI * c) - 1) / 2
}

function lerpNumber(a: number, b: number, u: number): number {
  return a + (b - a) * u
}

function lerpHueDegrees(fromHue: number, toHue: number, u: number): number {
  let diff = toHue - fromHue
  while (diff > 180) {
    diff -= 360
  }
  while (diff < -180) {
    diff += 360
  }
  const h = fromHue + diff * u
  return ((h % 360) + 360) % 360
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}
